import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

export const timeRegExp = new RegExp('^([0-1][0-9]|[2][0-3]):([0-5][0-9])$');
export const floatRegExp = new RegExp('^-?[0-9]+((.|,)[0-9]+)?$');
export const integerRegExp = new RegExp('^-?[0-9]*$');

const setOfFiltersExcludedErrors = ['whitespace', 'required', 'date_order'];

export function setFormGroupControlsErrors(errors: any | null, formGroup: AbstractControl, controlNames: string[]): void {
  controlNames.map(name => formGroup.get(name)).forEach(control => control.setErrors(errors));
}

export function markAsTouched(formGroup: AbstractControl, controlNames: string[], onlySelf: boolean = true): void {
  controlNames.map(name => formGroup.get(name)).forEach(control => control.markAsTouched({ onlySelf }));
}

export function validateAllFormFields(formGroup: FormGroup): void {
  Object.keys(formGroup.controls).forEach(field => {
    const control = formGroup.get(field);
    control.markAsTouched({ onlySelf: true });
    if (control instanceof FormGroup) {
      validateAllFormFields(control);
    } else if (control instanceof FormArray) {
      for (const nestedControl of control.controls) {
        nestedControl.markAsTouched({ onlySelf: true });
        if (nestedControl instanceof FormGroup) {
          validateAllFormFields(nestedControl);
        }
      }
    }
  });
}

export function validateControl(control: AbstractControl, emitEvent: boolean = true): void {
  control.markAsTouched();
  control.updateValueAndValidity({emitEvent});
}

/**
 * A conditional validator generator. Assigns a validator to the form control if the predicate function returns true on the moment of validation
 *
 * @example
 * Here if the myCheckbox is set to true, the myEmailField will be required and also the text will have to have the word 'mason' in the end.
 * If it doesn't satisfy these requirements, the errors will placed to the dedicated `illuminatiError` namespace.
 * Also the myEmailField will always have `maxLength`, `minLength` and `pattern` validators.
 * ngOnInit() {
 *   this.myForm = this.fb.group({
 *    myCheckbox: [''],
 *    myEmailField: ['', [
 *       Validators.maxLength(250),
 *       Validators.minLength(5),
 *       Validators.pattern(/.+@.+\..+/),
 *       conditionalValidator(() => this.myForm.get('myCheckbox').value,
 *                            Validators.compose([
 *                            Validators.required,
 *                            Validators.pattern(/.*mason/)
 *         ]),
 *        'illuminatiError')
 *        ]]
 *     })
 * }
 * @param errorNamespace optional argument that creates own namespace for the validation error
 */
export function conditionalValidator(predicate: BooleanFn, validator: ValidatorFn, errorNamespace?: string): ValidatorFn {
  return (formControl => {
    if (!formControl.parent) {
      return null;
    }
    let error = null;
    if (predicate()) {
      error = validator(formControl);
    }
    if (errorNamespace && error) {
      const customError = {};
      customError[errorNamespace] = error;
      error = customError;
    }
    return error;
  });
}

export function getErrorsForSetOfFilters(filterForm: AbstractControl, groupOrControlName: string = 'rootGroupOrControl'): ValidationErrors {
  if (filterForm instanceof FormControl)
    return filterForm.errors ?? null;

  if (filterForm instanceof FormGroup) {
    const formErrors = {};

    const formGroupErrors = filterForm.errors;
    if (deleteSetOfFiltersExcludedErrors(formGroupErrors))
      formErrors[groupOrControlName] = formGroupErrors;

    Object.keys(filterForm.controls).forEach(controlName => {
      const controlErrors = getErrorsForSetOfFilters(filterForm.get(controlName), controlName);
      if (deleteSetOfFiltersExcludedErrors(controlErrors))
        formErrors[controlName] = Object.values(controlErrors);
    });
    return Object.keys(formErrors).length > 0 ? formErrors : null;
  }
}

function deleteSetOfFiltersExcludedErrors(errors: ValidationErrors): boolean {
  if (!errors)
    return false;

  for (const excludedError of setOfFiltersExcludedErrors)
    delete errors[excludedError];
  return errors != null && Object.keys(errors).length > 0;
}

type BooleanFn = () => boolean;
